import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Helmet } from 'react-helmet-async';
import { getAdLink, formatWhatsAppNumber } from '../utils/adUtils';
import Spinner from '../components/Spinner';
import './VendorProfile.css';

const VendorProfile = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [vendor, setVendor] = useState(null);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('all');

    useEffect(() => {
        const fetchVendorData = async () => {
            try {
                let vendorData = null;
                let finalUserId = null;

                // First try to find by slug field
                const usersRef = collection(db, "users");
                const qSlug = query(usersRef, where("slug", "==", slug));
                const slugSnapshot = await getDocs(qSlug);

                if (!slugSnapshot.empty) {
                    vendorData = { id: slugSnapshot.docs[0].id, ...slugSnapshot.docs[0].data() };
                    finalUserId = vendorData.id;
                } else {
                    // Fallback to searching by document ID (uid) for older links
                    const userDocRef = doc(db, "users", slug);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        vendorData = { id: userDoc.id, ...userDoc.data() };
                        finalUserId = vendorData.id;
                    }
                }

                if (vendorData) {
                    setVendor(vendorData);
                    // Fetch all ads by this user
                    const q = query(collection(db, "ads"), where("userId", "==", finalUserId));
                    const querySnapshot = await getDocs(q);

                    // Also check total system ads for high-traffic auto-approval
                    const totalAdsSnapshot = await getDocs(collection(db, "ads"));
                    const totalCount = totalAdsSnapshot.size;

                    const fetchedAds = querySnapshot.docs.map(d => ({
                        id: d.id,
                        ...d.data()
                    })).filter(ad => totalCount >= 50 || ad.status !== 'pending');
                    // Sort by createdAt descending
                    fetchedAds.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
                    setAds(fetchedAds);
                }
            } catch (error) {
                console.error("Error fetching vendor data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (slug) fetchVendorData();
    }, [slug]);

    const filteredAds = activeFilter === 'all'
        ? ads
        : ads.filter(ad => ad.category === activeFilter);

    const categories = ['all', ...new Set(ads.map(ad => ad.category).filter(Boolean))];

    if (loading) {
        return <Spinner fullPage message="Loading vendor profile..." />;
    }

    if (!vendor || vendor.userType !== 'Corporate') {
        return (
            <div className="vendor-not-found">
                <i className="fa-solid fa-store-slash"></i>
                <h2>Vendor Not Found</h2>
                <p>This vendor profile doesn't exist or is an individual seller profile.</p>
                <button onClick={() => navigate('/')} className="vendor-btn-home">Go Home</button>
            </div>
        );
    }

    const isCorporate = vendor.userType === 'Corporate';
    const displayName = isCorporate ? (vendor.vendorName || vendor.fullName) : vendor.fullName;
    const memberSince = vendor.createdAt?.toDate
        ? new Date(vendor.createdAt.toDate()).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
        : vendor.createdAt instanceof Date
            ? vendor.createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
            : 'Recently';

    const canonicalUrl = `https://riyamaga.site/${vendor.slug || slug}`;
    const ogImage = vendor.coverPhoto || vendor.logo || 'https://riyamaga.site/og-default.jpg';
    const metaDescription = vendor.bio
        ? `${vendor.bio} | ${ads.length} vehicles listed on RiyaMaga.`
        : `${displayName} is a trusted vehicle dealer on RiyaMaga Sri Lanka. Browse ${ads.length} listings including cars, vans, bikes and more. Contact them directly on RiyaMaga.`;
    const metaKeywords = [
        displayName,
        'vehicle dealer Sri Lanka',
        'car dealer Sri Lanka',
        'RiyaMaga vendor',
        ...(ads.slice(0, 5).map(a => a.vehicleMake).filter(Boolean)),
        ...(ads.slice(0, 5).map(a => a.vehicleType).filter(Boolean)),
    ].filter((v, i, a) => a.indexOf(v) === i).join(', ');

    // JSON-LD structured data for rich search results
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "AutoDealer",
        "name": displayName,
        "url": canonicalUrl,
        "telephone": vendor.phone || '',
        "image": ogImage,
        "description": metaDescription,
        "address": {
            "@type": "PostalAddress",
            "addressCountry": "LK"
        },
        "numberOfEmployees": ads.length > 0 ? { "@type": "QuantitativeValue", "value": ads.length } : undefined,
        "sameAs": [canonicalUrl]
    };

    return (
        <div className="vendor-profile-page">
            <Helmet>
                <title>{`${displayName} | Vehicle Dealer Sri Lanka | RiyaMaga`}</title>
                <meta name="description" content={metaDescription} />
                <meta name="keywords" content={metaKeywords} />
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph */}
                <meta property="og:type" content="business.business" />
                <meta property="og:title" content={`${displayName} - Vehicle Dealer | RiyaMaga`} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content="RiyaMaga" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${displayName} | RiyaMaga`} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={ogImage} />

                {/* JSON-LD */}
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>

            {/* Cover Photo Section */}
            <div className="vendor-cover-section">
                {isCorporate && vendor.coverPhoto ? (
                    <img src={vendor.coverPhoto} alt="Cover" className="vendor-cover-img" />
                ) : (
                    <div className="vendor-cover-placeholder">
                        <div className="cover-pattern"></div>
                    </div>
                )}
                <div className="vendor-cover-overlay"></div>
            </div>

            {/* Vendor Info Card */}
            <div className="vendor-info-wrapper">
                <div className="vendor-info-card">
                    <div className="vendor-avatar-section">
                        {isCorporate && vendor.logo ? (
                            <img src={vendor.logo} alt={displayName} className="vendor-avatar-img" />
                        ) : (
                            <div className="vendor-avatar-placeholder">
                                <i className={isCorporate ? "fa-solid fa-building" : "fa-solid fa-user"}></i>
                            </div>
                        )}
                        {isCorporate && (
                            <span className="vendor-badge-corporate">
                                <i className="fa-solid fa-check-circle"></i> Corporate
                            </span>
                        )}
                    </div>
                    <div className="vendor-details">
                        <h1 className="vendor-display-name">{displayName || 'Unknown Vendor'}</h1>
                        <div className="vendor-meta-info">
                            {vendor.location && (
                                <span className="vendor-meta-item">
                                    <i className="fa-solid fa-location-dot"></i> {vendor.location}
                                </span>
                            )}
                            <span className="vendor-meta-item">
                                <i className="fa-solid fa-calendar-check"></i> Member since {memberSince}
                            </span>
                            <span className="vendor-meta-item">
                                <i className="fa-solid fa-rectangle-ad"></i> {ads.length} Ads Listed
                            </span>
                        </div>
                    </div>
                    <div className="vendor-contact-actions">
                        {vendor.phone && (
                            <a href={`tel:${vendor.phone}`} className="vendor-action-btn vendor-call-btn">
                                <i className="fa-solid fa-phone"></i> Call
                            </a>
                        )}
                        {vendor.phone && (
                            <a
                                href={`https://wa.me/${formatWhatsAppNumber(vendor.phone)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="vendor-action-btn vendor-whatsapp-btn"
                            >
                                <i className="fa-brands fa-whatsapp"></i> WhatsApp
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Ads Section */}
            <div className="vendor-ads-section">
                <div className="vendor-ads-header">
                    <h2 className="vendor-ads-title">
                        <i className="fa-solid fa-grid-2"></i> All Listings
                        <span className="vendor-ads-count">{filteredAds.length}</span>
                    </h2>
                    {categories.length > 1 && (
                        <div className="vendor-filter-tabs">
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    className={`vendor-filter-tab ${activeFilter === cat ? 'active' : ''}`}
                                    onClick={() => setActiveFilter(cat)}
                                >
                                    {cat === 'all' ? 'All' : cat}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {filteredAds.length > 0 ? (
                    <div className="vendor-ads-grid">
                        {filteredAds.map(ad => (
                            <div
                                key={ad.id}
                                className="vendor-ad-card"
                                onClick={() => navigate(getAdLink(ad))}
                            >
                                <div className="vendor-ad-img-wrap">
                                    <img
                                        src={ad.image || 'https://placehold.co/600x400/1e293b/ffffff?text=No+Image'}
                                        alt={ad.title}
                                        className="vendor-ad-img"
                                    />
                                    {ad.category && (
                                        <span className="vendor-ad-category-badge">{ad.category}</span>
                                    )}
                                </div>
                                <div className="vendor-ad-info">
                                    <h3 className="vendor-ad-title">
                                        {ad.title || `${ad.vehicleMake || ''} ${ad.model || ''}`.trim() || 'Vehicle Ad'}
                                        {ad.manufacturedYear && <span className="ad-title-year"> ({ad.manufacturedYear})</span>}
                                    </h3>
                                    <div className="vendor-ad-specs">
                                        {ad.vehicleMake && <span>{ad.vehicleMake}</span>}
                                        {ad.model && <span>{ad.model}</span>}
                                        {ad.manufacturedYear && <span>{ad.manufacturedYear}</span>}
                                    </div>
                                    <div className="vendor-ad-bottom">
                                        <span className="vendor-ad-price">Rs. {ad.price}</span>
                                        {ad.vehicleCondition && (
                                            <span className="ad-condition">{ad.vehicleCondition}</span>
                                        )}
                                        {ad.location && (
                                            <span className="vendor-ad-location">
                                                <i className="fa-solid fa-location-dot"></i> {ad.location}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="vendor-no-ads">
                        <i className="fa-solid fa-box-open"></i>
                        <p>No ads listed yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VendorProfile;
